import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Utensils, Wine, LayoutGrid, CheckCircle2, RotateCcw,
  History, Clock, X, ChefHat, Bell,
} from "lucide-react";

const STATIONS = [
  { key: "all",     label: "All",     icon: LayoutGrid, num: ".00" },
  { key: "bar",     label: "Bar",     icon: Wine,       num: ".01" },
  { key: "kitchen", label: "Kitchen", icon: ChefHat,    num: ".02" },
];

const NEXT_STATUS = {
  new: "accepted",
  accepted: "preparing",
  preparing: "ready",
  ready: "served",
};

const NEXT_LABEL = {
  new: "Accept",
  accepted: "Start prep",
  preparing: "Mark ready",
  ready: "Bump / Serve",
};

const STATUS_LABEL = {
  new: "New",
  accepted: "Accepted",
  preparing: "In prep",
  ready: "Ready",
  served: "Served",
  cancelled: "Cancelled",
};

const fmtAge = (s) => {
  const t = Math.max(0, s || 0);
  const m = Math.floor(t / 60);
  const sec = t % 60;
  if (m >= 60) return `${Math.floor(m / 60)}h ${m % 60}m`;
  return `${m}:${String(sec).padStart(2, "0")}`;
};

export default function KDSPage() {
  const { branchId } = useAuth();
  const [station, setStation] = useState("all");
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [recall, setRecall] = useState([]);
  const [showRecall, setShowRecall] = useState(false);
  const [cancelling, setCancelling] = useState(null); // { ticket, reason }
  const [loading, setLoading] = useState(true);
  const [nowTs, setNowTs] = useState(Date.now());
  const knownIds = useRef(new Set());
  const [flashIds, setFlashIds] = useState(new Set());

  const load = async () => {
    if (!branchId) return;
    try {
      const [tks, st] = await Promise.all([
        api.get("/tickets", { params: { branch_id: branchId, station, limit: 500 } }),
        api.get("/kds/stats", { params: { branch_id: branchId } }),
      ]);
      // Detect new arrivals for a subtle flash animation.
      const incomingIds = new Set(tks.data.map((t) => t.id));
      const fresh = tks.data.filter((t) => !knownIds.current.has(t.id) && t.status === "new");
      knownIds.current = incomingIds;
      if (fresh.length > 0) {
        setFlashIds((prev) => {
          const next = new Set(prev);
          fresh.forEach((f) => next.add(f.id));
          return next;
        });
        // Clear the flash class after 3s.
        setTimeout(() => {
          setFlashIds((prev) => {
            const next = new Set(prev);
            fresh.forEach((f) => next.delete(f.id));
            return next;
          });
        }, 3000);
      }
      setTickets(tks.data);
      setStats(st.data);
    } catch (_) {
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    knownIds.current = new Set();
    load();
    const iv = setInterval(load, 4000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, station]);

  // Local ticking clock so age counters go up between polls.
  useEffect(() => {
    const iv = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(iv);
  }, []);

  const openRecall = async () => {
    if (!branchId) return;
    setShowRecall(true);
    try {
      const { data } = await api.get("/tickets/recall", { params: { branch_id: branchId, station } });
      setRecall(data);
    } catch (_) { setRecall([]); }
  };

  const doRecall = async (t) => {
    try {
      await api.post(`/tickets/${t.id}/recall`);
      toast.success(`Ticket ${t.order_number} recalled to the board`);
      setShowRecall(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const advance = async (t) => {
    const next = NEXT_STATUS[t.status];
    if (!next) return;
    try {
      await api.post(`/tickets/${t.id}/status`, { status: next });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const cancel = (t) => setCancelling({ ticket: t, reason: "" });

  const doCancel = async () => {
    if (!cancelling || !cancelling.reason.trim()) return;
    try {
      await api.post(`/tickets/${cancelling.ticket.id}/status`, {
        status: "cancelled", reason: cancelling.reason.trim(),
      });
      toast.success(`Ticket ${cancelling.ticket.order_number} cancelled`);
      setCancelling(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleLine = async (t, idx) => {
    const cur = !!t.items?.[idx]?.line_ready;
    // Optimistic
    setTickets((ts) => ts.map((x) => x.id === t.id ? {
      ...x,
      items: x.items.map((it, i) => i === idx ? { ...it, line_ready: !cur } : it),
    } : x));
    try {
      await api.post(`/tickets/${t.id}/lines/${idx}/toggle`, { ready: !cur });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      load();
    }
  };

  const grouped = useMemo(() => {
    const g = { new: [], accepted: [], preparing: [], ready: [] };
    for (const t of tickets) if (g[t.status]) g[t.status].push(t);
    return g;
  }, [tickets]);

  const stationStats = useMemo(() => {
    if (!stats?.stations) return null;
    if (station === "all") {
      return stats.stations.reduce((acc, s) => ({
        total: acc.total + s.total,
        preparing: acc.preparing + s.preparing,
        ready: acc.ready + s.ready,
      }), { total: 0, preparing: 0, ready: 0 });
    }
    return stats.stations.find((s) => s.station === station) || { total: 0, preparing: 0, ready: 0 };
  }, [stats, station]);

  return (
    <div className="p-8 lg:p-14 min-h-full" data-testid="kds-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-8 animate-fade-up">
        <div>
          <div className="section-eyebrow ink-subtle">Point of Sale · Live queue</div>
          <h1 className="mt-4 display-heading text-4xl md:text-5xl ink">Kitchen &amp; Bar display</h1>
          <p className="mt-3 ink-muted text-sm max-w-md leading-relaxed">
            Live prep tickets, auto-refreshed every four seconds. Tap a line to mark it ready — the whole ticket promotes itself once every line is done.
          </p>
        </div>
        <div className="text-right">
          <div className="section-eyebrow">Oldest wait</div>
          <div
            className={`mt-3 display-heading text-5xl mono ${
              (stats?.oldest_age_seconds ?? 0) > 480 ? "text-red-500"
              : (stats?.oldest_age_seconds ?? 0) > 180 ? "text-amber-500" : "ink"
            }`}
            data-testid="kds-oldest-age"
          >
            {fmtAge(stats?.oldest_age_seconds ?? 0)}
          </div>
          <div className="mt-1 tiny-label ink-subtle">
            Avg prep {fmtAge(stats?.avg_prep_seconds_last_2h ?? 0)} · {stats?.served_last_2h ?? 0} served last 2h
          </div>
        </div>
      </div>

      {/* Stats strip */}
      {branchId && stationStats && (
        <div className="mt-14 grid grid-cols-3 md:grid-cols-6 gap-x-8 gap-y-6 animate-fade-up animate-fade-up-1">
          <Metric num=".01" label={`On the board${station === "all" ? "" : ` · ${station}`}`} value={stationStats.total} />
          <Metric num=".02" label={`In prep${station === "all" ? "" : ` · ${station}`}`} value={stationStats.preparing} />
          <Metric num=".03" label={`Ready to serve${station === "all" ? "" : ` · ${station}`}`} value={stationStats.ready} accent />
          <Metric num=".04" label="New (bar)" value={stats?.stations?.find((s)=>s.station==="bar")?.new ?? 0} />
          <Metric num=".05" label="New (kitchen)" value={stats?.stations?.find((s)=>s.station==="kitchen")?.new ?? 0} />
          <Metric num=".06" label="Served last 2h" value={stats?.served_last_2h ?? 0} />
        </div>
      )}

      {/* Station tabs + controls */}
      <div className="mt-14 flex flex-wrap items-center justify-between gap-4 animate-fade-up animate-fade-up-2">
        <div className="flex items-center gap-1 border hairline-soft rounded-full p-1">
          {STATIONS.map((s) => (
            <button
              key={s.key}
              onClick={() => setStation(s.key)}
              data-testid={`kds-station-${s.key}`}
              className={`h-9 px-4 rounded-full text-[11px] uppercase tracking-[0.24em] font-bold flex items-center gap-2 transition-all ${
                station === s.key
                  ? "bg-foreground text-background shadow-sm"
                  : "text-foreground/60 hover:text-foreground"
              }`}
            >
              <s.icon size={12}/>{s.label}
              {stats?.stations && s.key !== "all" && (
                <span className="mono text-[10px] opacity-70">
                  {stats.stations.find((x)=>x.station===s.key)?.total ?? 0}
                </span>
              )}
            </button>
          ))}
        </div>
        <button
          onClick={openRecall}
          data-testid="kds-recall-btn"
          className="btn-pill btn-pill-outline h-9 text-[10px]"
        >
          <History size={12}/> Recall recent
        </button>
      </div>

      {/* Board */}
      <div className="mt-8 animate-fade-up animate-fade-up-3">
        {!branchId && (
          <div className="border hairline-soft rounded-md p-12 text-center ink-subtle text-sm">
            Select a branch to view live tickets.
          </div>
        )}

        {branchId && tickets.length === 0 && !loading && (
          <div className="border hairline-soft rounded-md p-16 text-center" data-testid="kds-empty">
            <CheckCircle2 size={40} className="mx-auto ink" strokeWidth={1.2}/>
            <div className="mt-5 display-heading text-2xl ink">All clear.</div>
            <div className="mt-2 ink-subtle text-sm">
              Nothing pending on the {station === "all" ? "board" : `${station} station`} right now.
            </div>
          </div>
        )}

        {branchId && tickets.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {tickets.map((t) => (
              <TicketCard
                key={t.id}
                t={t}
                nowTs={nowTs}
                flash={flashIds.has(t.id)}
                onAdvance={advance}
                onCancel={cancel}
                onToggleLine={toggleLine}
              />
            ))}
          </div>
        )}
      </div>

      {/* Recall drawer */}
      {showRecall && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="recall-drawer">
          <div className="absolute inset-0 bg-black/70" onClick={()=>setShowRecall(false)}/>
          <div className="relative w-full max-w-md surface-2 border-l hairline h-full p-6 overflow-y-auto shadow-2xl">
            <div className="flex items-start justify-between">
              <div>
                <div className="section-eyebrow">KDS · Recall</div>
                <h2 className="mt-2 display-heading text-2xl ink">Recently served</h2>
                <p className="mt-2 ink-muted text-xs">Last 30 minutes on the {station === "all" ? "whole board" : `${station} station`}. Tap to bring back onto the board.</p>
              </div>
              <button onClick={()=>setShowRecall(false)} className="tiny-label ink-subtle hover:ink"><X size={16}/></button>
            </div>
            <div className="mt-6 divide-y hairline-soft border-y hairline-soft">
              {recall.length === 0 && (
                <div className="py-8 text-center ink-subtle text-xs">No recent tickets to recall.</div>
              )}
              {recall.map((t) => (
                <div key={t.id} className="py-3 flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="mono ink text-sm font-semibold">{t.order_number}</div>
                    <div className="text-[11px] ink-subtle truncate mt-0.5">
                      {(t.items || []).map((i) => `${i.quantity}× ${i.name}`).join(" · ")}
                    </div>
                  </div>
                  <span className={`text-[9px] uppercase tracking-[0.22em] font-bold px-2 py-0.5 rounded-full border ${
                    t.status === "cancelled" ? "text-red-500 border-red-500/40" : "text-foreground/60 border-foreground/20"
                  }`}>{STATUS_LABEL[t.status]}</span>
                  <button
                    onClick={() => doRecall(t)}
                    data-testid={`kds-recall-do-${t.id}`}
                    className="btn-pill btn-pill-outline h-8 px-3 text-[10px]"
                  >
                    <RotateCcw size={11}/> Recall
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* Cancel dialog */}
      {cancelling && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="kds-cancel-dialog">
          <div className="absolute inset-0 bg-black/70" onClick={() => setCancelling(null)}/>
          <div className="relative w-full max-w-md surface-2 border hairline rounded-lg p-6 shadow-2xl">
            <div className="section-eyebrow">KDS · Cancel ticket</div>
            <h2 className="mt-2 display-heading text-2xl ink">Cancel {cancelling.ticket.order_number}?</h2>
            <p className="mt-2 ink-muted text-xs">
              This removes the ticket from the board. The reason is stored on the audit trail — please be specific.
            </p>
            <label className="block mt-6">
              <div className="tiny-label">Reason (required)</div>
              <textarea
                autoFocus
                rows={3}
                value={cancelling.reason}
                onChange={(e) => setCancelling({ ...cancelling, reason: e.target.value })}
                data-testid="kds-cancel-reason"
                placeholder="e.g. wrong table, guest walked out, item unavailable"
                className="mt-2 w-full rounded-md border hairline surface-card px-3 py-2 ink text-sm focus:border-foreground/50 outline-none"
              />
            </label>
            <div className="mt-6 flex gap-2">
              <button
                onClick={() => setCancelling(null)}
                className="btn-pill btn-pill-outline h-11 flex-1"
                data-testid="kds-cancel-abort"
              >
                Keep on the board
              </button>
              <button
                onClick={doCancel}
                disabled={!cancelling.reason.trim()}
                data-testid="kds-cancel-confirm"
                className="btn-pill btn-pill-solid h-11 flex-1 disabled:opacity-50"
              >
                Cancel ticket
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Metric({ num, label, value, accent }) {
  return (
    <div data-testid={`kds-metric-${label.toLowerCase().replace(/[^\w]+/g,"-")}`}>
      <div className="flex items-center justify-between">
        <div className="section-eyebrow">{label}</div>
        <span className="section-num">{num}</span>
      </div>
      <div className={`mt-3 ink mono text-3xl font-semibold tabular-nums ${accent ? "border-l-2 border-foreground pl-3" : ""}`}>
        {value}
      </div>
    </div>
  );
}

function TicketCard({ t, nowTs, flash, onAdvance, onCancel, onToggleLine }) {
  const created = new Date(t.created_at).getTime();
  const age = Math.max(0, Math.floor((nowTs - created) / 1000));
  const bucket = age < 180 ? "fresh" : age < 480 ? "aging" : "stale";
  const ageClass = bucket === "stale"
    ? "text-red-500 border-red-500/40"
    : bucket === "aging"
      ? "text-amber-500 border-amber-500/40"
      : "text-foreground border-foreground/25";

  const stationIcon = t.station === "kitchen" ? Utensils : Wine;
  const StationIcon = stationIcon;

  const next = NEXT_STATUS[t.status];
  const allReady = (t.items || []).length > 0 && (t.items || []).every((i) => i.line_ready);

  return (
    <div
      data-testid={`ticket-${t.id}`}
      className={`relative border hairline surface-card rounded-lg overflow-hidden transition-shadow ${
        t.status === "ready" ? "ring-2 ring-emerald-500/40" : ""
      } ${flash ? "kds-flash" : ""}`}
    >
      {/* Left aging rail */}
      <div className={`absolute inset-y-0 left-0 w-1 ${
        bucket === "stale" ? "bg-red-500" : bucket === "aging" ? "bg-amber-500" : "bg-foreground/30"
      }`}/>

      {/* Head */}
      <div className="p-4 pl-5 border-b hairline-soft flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <StationIcon size={13} className="ink-subtle"/>
            <span className="tiny-label ink-subtle">{t.station}</span>
            {t.ticket_type === "vip_package_inclusion" && (
              <span className="text-[9px] uppercase tracking-[0.22em] font-bold px-2 py-0.5 rounded-full border text-amber-500 border-amber-500/40 bg-amber-500/5"
                    data-testid={`kds-vip-badge-${t.id}`}>
                VIP INCLUSION · {t.package_code}
              </span>
            )}
          </div>
          <div className="mono ink text-lg font-semibold mt-1">{t.order_number}</div>
        </div>
        <div className="text-right">
          <div className={`inline-flex items-center gap-1 mono text-[11px] font-semibold px-2 py-0.5 rounded-full border ${ageClass}`}>
            <Clock size={10}/> {fmtAge(age)}
          </div>
          <div className="mt-1 text-[10px] uppercase tracking-[0.22em] ink-subtle font-semibold">
            {STATUS_LABEL[t.status] || t.status}
          </div>
        </div>
      </div>

      {/* Lines */}
      <div className="p-4 pl-5 space-y-2">
        {(t.items || []).map((it, idx) => (
          <button
            key={idx}
            type="button"
            onClick={() => onToggleLine(t, idx)}
            data-testid={`ticket-line-${t.id}-${idx}`}
            className={`w-full text-left flex items-start gap-3 p-2 -mx-2 rounded-md border transition-colors ${
              it.line_ready
                ? "border-foreground/50 bg-foreground/[0.04] line-through opacity-75"
                : "border-transparent hover:bg-foreground/[0.03]"
            }`}
          >
            <div className={`mt-0.5 h-5 w-5 rounded-md border-2 flex items-center justify-center shrink-0 ${
              it.line_ready ? "border-foreground bg-foreground text-background" : "border-foreground/30"
            }`}>
              {it.line_ready && <CheckCircle2 size={12}/>}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm ink font-medium">
                <span className="mono font-bold">{it.quantity}×</span> {it.name}
              </div>
              {(it.modifiers || []).length > 0 && (
                <div className="text-[11px] ink-subtle mt-0.5">
                  {it.modifiers.map((m) => m.name).join(" · ")}
                </div>
              )}
              {it.notes && (
                <div className="text-[11px] text-amber-500 mt-0.5 flex items-center gap-1">
                  <Bell size={10}/> {it.notes}
                </div>
              )}
            </div>
          </button>
        ))}
      </div>

      {/* Actions */}
      <div className="border-t hairline-soft p-3 pl-5 flex items-center gap-2">
        {next && (
          <button
            onClick={() => onAdvance(t)}
            data-testid={`ticket-advance-${t.id}`}
            className={`flex-1 btn-pill h-10 text-[11px] ${
              allReady && t.status === "ready"
                ? "btn-pill-solid"
                : t.status === "ready" ? "btn-pill-solid" : "btn-pill-outline"
            }`}
          >
            {NEXT_LABEL[t.status]}
          </button>
        )}
        <button
          onClick={() => onCancel(t)}
          data-testid={`ticket-cancel-${t.id}`}
          className="h-10 px-3 rounded-full border border-red-500/30 text-red-500 hover:bg-red-500/5 text-[10px] uppercase tracking-[0.24em] font-bold transition-colors"
        >
          Cancel
        </button>
      </div>

      <style>{`
        @keyframes kdsFlash {
          0% { box-shadow: 0 0 0 0 hsl(var(--foreground) / 0.35); }
          100% { box-shadow: 0 0 0 12px hsl(var(--foreground) / 0); }
        }
        .kds-flash { animation: kdsFlash 1.6s ease-out 2; }
      `}</style>
    </div>
  );
}
